'use client'

import { useTranslations } from 'next-intl'
import { ReactNode, use } from 'react'

import SeeAllLink from '@/app/[locale]/(worlds)/components/SeeAllLink'
import Swimlane, { SwimlaneSkeleton } from '@/app/[locale]/(worlds)/components/Swimlane'
import CreatorAvatar, { CreatorAvatarSkeleton } from '@/components/CreatorAvatar'
import { useDraggingScrollMotion } from '@/hooks/useDraggingScrollMotion'
import { hasVerifiedCreatorBadge } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { Creator } from '@/services/cms'

type FeatureCreatorsProps = {
  creatorsPromise: Promise<Creator[]>
  title?: ReactNode
  titleAction?: ReactNode
  sectionClassName?: string
  scrollContainerClassName?: string
  contentClassName?: string
}

export default function FeatureCreators({
  creatorsPromise,
  title,
  titleAction,
  sectionClassName,
  scrollContainerClassName,
  contentClassName,
}: FeatureCreatorsProps) {
  const t = useTranslations()
  const recommendCreators = use(creatorsPromise)

  if (recommendCreators.length === 0) {
    return null
  }

  const defaultTitle = t('worlds-landing.feature-creators.title')

  const defaultTitleAction = (
    <SeeAllLink href="/creators" label={t('worlds-landing.feature-creators.see-all')} />
  )

  return (
    <Swimlane
      data-section="featured-creators"
      title={title ?? defaultTitle}
      titleAction={titleAction !== undefined ? titleAction : defaultTitleAction}
      className={sectionClassName}
      scrollContainerClassName={scrollContainerClassName}
      renderContent={(scrollRef) => (
        <CreatorScrollContainer
          scrollRef={scrollRef}
          creators={recommendCreators}
          contentClassName={contentClassName}
        />
      )}
    />
  )
}

function CreatorScrollContainer({
  scrollRef,
  creators,
  contentClassName,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>
  creators: Creator[]
  contentClassName?: string
}) {
  useDraggingScrollMotion(scrollRef)

  return (
    <div
      ref={scrollRef}
      className={cn(
        'no-scrollbar flex cursor-grab gap-4 overflow-x-auto overflow-y-hidden pl-4 select-none md:pl-4 lg:pl-0',
        contentClassName,
      )}
    >
      {creators.map((creator, index) => (
        <div
          key={creator.id}
          className={index === creators.length - 1 ? 'mr-4 shrink-0 md:mr-4 lg:mr-6' : 'shrink-0'}
        >
          <CreatorAvatar
            communityUserId={creator.community_user_id}
            headIconUrl={creator.head_icon_url}
            displayName={creator.display_name}
            isVerified={hasVerifiedCreatorBadge(creator)}
            size="sm"
            disableDrag
          />
        </div>
      ))}
    </div>
  )
}

export function FeatureCreatorsSkeleton({
  sectionClassName,
  contentClassName,
}: {
  sectionClassName?: string
  contentClassName?: string
}) {
  return (
    <SwimlaneSkeleton showTitleAction className={sectionClassName}>
      <div
        className={cn(
          'no-scrollbar flex gap-6 overflow-x-auto pl-4 md:pl-4 lg:pl-0',
          contentClassName,
        )}
      >
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={`skeleton-${index}`} className="shrink-0">
            <CreatorAvatarSkeleton />
          </div>
        ))}
      </div>
    </SwimlaneSkeleton>
  )
}
